import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { ParticipantService } from './participant.service';
import { Paginator } from "../../common/models/paginator";
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { SimpleMatDataSource } from '../../common/providers/simple-mat-data-source';
import { UrlFormatter } from '../../common/http/url-formatter';
import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { Subject, Subscription } from 'rxjs';
import { DiscussionService } from '../discussion/discussion.service';
import { SnackbarData } from '../../common/snackbar/snackbar-data';
import { ApiError } from '../../common/error/api-error';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ConfigProvider} from "../../config-provider.service";
import { DiscussionModel } from '../../common/models/discussion-model';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { TranslateModule } from "@ngx-translate/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { FlexLayoutModule } from "@angular/flex-layout";
import { NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatSortModule } from "@angular/material/sort";
import { Organization } from "../../common/models/organization";
import { User } from "../../common/models/user";

@Component({
  selector: 'app-chat-discussion-participant-dialog',
  templateUrl: './discussion-participant-dialog.component.html',
  styleUrls: ['./discussion-participant-dialog.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    FlexLayoutModule,
    NgIf,
    MatPaginatorModule,
    MatButtonModule,
    MatSortModule
  ],
  providers: [ParticipantService, DiscussionService]
})
export class DiscussionParticipantDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    public organization: Organization = {} as Organization;
    public isLoading = true;
    public isSubmitting = false;
    public form: FormGroup;
    public user: User = {} as User;
    public paginator: Paginator = {} as Paginator;
    public selection = new SelectionModel<number>(false, []); // set multiple to "true" to create chat discussion group
    // public columns: string[] = ['user', 'action'];
    public columns: string[] = ['user'];
    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    @ViewChild(MatPaginator, {static: false}) public matPaginator: MatPaginator | null = null;

    public userFilterKeyUp = new Subject<KeyboardEvent>();
    private userFilterKeyUpSubscription: Subscription = new Subscription();

    private subscriptions: Subscription[] = [];

    constructor(
        private discussionService: DiscussionService,
        private urlFormatter: UrlFormatter,
        private participantService: ParticipantService,
        private fb: FormBuilder,
        private utils: UtilsProvider,
        public dialogRef: MatDialogRef<DiscussionParticipantDialogComponent>,
        public conf: ConfigProvider,
        @Inject(MAT_DIALOG_DATA) public dialogData:any
    ) {
        this.user = dialogData.user;
        this.organization = dialogData.organization;

        this.urlFormatter.query.clear();

        this.userFilter();

        this.form = this.fb.group({});
    }

    public ngOnInit(): void {
        this.populateUsers(this.urlFormatter);

        // dynamically add discussion name form control
        this.subscriptions.push(
            this.selection.changed.subscribe((selection: SelectionChange<any>) => {
                this.addRemoveDiscussionNameControl();
            })
        );
    }

    public hasMultipleUserSelected(): boolean {
        return !this.selection.isEmpty() && this.selection.selected.length > 1;
    }

    public ngAfterViewInit(): void {
        this.dataSource.paginator = this.matPaginator;
    }

    public onPaginateChange(event:any) {
        this.urlFormatter.query.set('page', event.pageIndex + 1);

        this.populateUsers(this.urlFormatter);
    }

    /**
     * For single user selection only
     *
     * @param data
     */
    public onUserSelected(data:any) {
        this.selection.select(data.id);

        this.onSave();
    }

    public onSave() {
        this.isSubmitting = true;

        if (this.form.valid) {
            const data = new SnackbarData('ChatDiscussionCreated');

            this.subscriptions.push(
                this.discussionService.save(this.user, this.organization, this.selection.selected, this.form.value.name).subscribe({
                    next: (discussion: DiscussionModel) => {
                        data.setData(discussion);
                        this.dialogRef.close(discussion);
                    },
                    error: (error: ApiError) => {
                        data.setError(error);
                        this.dialogRef.close(data);
                    }
                })
            );
        } else {
            this.utils.validateAllFormFields(this.form);
            this.isSubmitting = false;
        }
    }

    public ngOnDestroy(): void {
        this.userFilterKeyUpSubscription.unsubscribe();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get f() {
        return this.form.controls;
    }

    /**
     * Search filter
     */
    private userFilter(): void {
        this.subscriptions.push(
            this.userFilterKeyUpSubscription = this.userFilterKeyUp.pipe(
                debounceTime(250),
                distinctUntilChanged(),
                map((e: KeyboardEvent) => (e.target as HTMLInputElement).value),
            ).subscribe((value) => {
                this.urlFormatter.query.set('page', 1);
                this.urlFormatter.query.set('q', value);

                this.populateUsers(this.urlFormatter);
            })
        );
    }

    private populateUsers(urlFormatter: UrlFormatter): void {
        this.subscriptions.push(
            this.participantService.findAllByUserOrganizationsPaginated(this.user, urlFormatter, this.organization)
                .pipe(
                    finalize(() => this.isLoading = false)
                )
                .subscribe((paginatedUsers: Paginator) => {
                    this.paginator = paginatedUsers;
                    this.dataSource.replaceAllWith(this.paginator.data);
                })
        );
    }

    private addRemoveDiscussionNameControl() {
        if (this.hasMultipleUserSelected() && !this.form.contains('name')) {
            this.form.addControl('name', new FormControl(null, [Validators.required, Validators.maxLength(50)]));
        } else if (!this.hasMultipleUserSelected()) {
            this.form.removeControl('name');
        }
    }
}
